import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import toast from 'react-hot-toast';
import { api } from '../../shared/service/dataprovider';
import tokens from '../../shared/theme/tokens';
import Panel from '../../components/Panel';
import StatusChip from '../../components/StatusChip';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const fmtUsd = v => (typeof v === 'number' && !Number.isNaN(v) ? usd.format(v) : '—');

// Maps the per-report `status` to a StatusChip tone key.
const statusColor = { ok: 'success', partial: 'warning', error: 'error', failed: 'error' };

// The six overridable TIC figures, in form order. `field` indexes into the
// per-member `computed`/`override` objects the API returns; `key` is the
// override category persisted to proofup_income_overrides.
const TIC_CATEGORIES = [
  { key: 'income_a', field: 'a', label: 'Employment / wages (A)' },
  { key: 'income_b', field: 'b', label: 'Social Security / pensions (B)' },
  { key: 'income_c', field: 'c', label: 'Public assistance (C)' },
  { key: 'income_d', field: 'd', label: 'Other income (D)' },
  { key: 'asset_cash_value', field: 'assetCash', label: 'Asset cash value' },
  { key: 'asset_income', field: 'assetIncome', label: 'Asset annual income' },
];
// The two rows that carry a declared (applicant-typed) figure to reconcile.
const ASSET_FIELDS = { assetCash: 'asset_cash_value', assetIncome: 'asset_income' };

// Per-income-source line categories (flag-on mode), with display label and the
// A–D bucket they roll up into. Order matches the server's LINE_INCOME_CATEGORIES.
const LINE_META = {
  employment_income: { label: 'Employment / wages', bucket: 'a' },
  self_employment: { label: 'Self-employment', bucket: 'a' },
  social_security_retirement_or_ssdi: { label: 'Social Security / SSDI', bucket: 'b' },
  ssi: { label: 'SSI', bucket: 'b' },
  trusts_annuities_pensions: { label: 'Pensions / annuities / trusts', bucket: 'b' },
  public_assistance: { label: 'Public assistance', bucket: 'c' },
  child_support: { label: 'Child support', bucket: 'd' },
  other_income: { label: 'Other income', bucket: 'd' },
};
const BUCKET_LABEL = { a: 'Employment / wages (A)', b: 'Social Security / pensions (B)', c: 'Public assistance (C)', d: 'Other income (D)' };
const ASSET_ROWS = [
  { key: 'asset_cash_value', field: 'assetCash', label: 'Asset cash value' },
  { key: 'asset_income', field: 'assetIncome', label: 'Asset annual income' },
];

const cents = n => Math.round((Number(n) || 0) * 100);
// Parses a user-entered money string to a number; null when blank/invalid.
const parseMoney = s => {
  const t = String(s ?? '').replace(/[^0-9.]/g, '');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
// ProofUp's warning text embeds raw member uuids (e.g. member="85e6…"). Swap
// each uuid for the member's name when we know it, otherwise drop it to a
// neutral label — staff never see internal ids.
const sanitizeWarning = (text, memberNames = {}) => String(text ?? '').replace(UUID_RE, id => memberNames[id] || 'a household member');

// True when an applicant has a declared figure that differs from ProofUp's
// extraction and no staff override has decided which value carries to the TIC.
// This is now an advisory review cue only; it must not block verification or
// package release.
const INCOME_BUCKETS = ['a', 'b', 'c', 'd'];
const hasUnresolvedMismatch = (applicant, ticDisabled = false) => {
  if (ticDisabled) return false;
  const assetUnresolved = Object.keys(ASSET_FIELDS).some(field => {
    const declaredVal = applicant.declared ? applicant.declared[field] : null;
    if (declaredVal == null) return false;
    const computed = Number(applicant.computed?.[field] ?? 0) || 0;
    const overridden = applicant.override && applicant.override[field] != null;
    return cents(declaredVal) !== cents(computed) && !overridden;
  });
  // Income mismatch (server flags a bucket where ProofUp's extraction disagrees
  // with a declared amount) — resolved by an override on that bucket. A bucket
  // ProofUp simply didn't process is NOT a mismatch (declared carries cleanly).
  const incomeUnresolved = INCOME_BUCKETS.some(b => {
    const overridden = applicant.override && applicant.override[b] != null;
    return Boolean(applicant.incomeMismatch?.[b]) && !overridden;
  });
  return assetUnresolved || incomeUnresolved;
};

// Income-verification panel for the site team. Shows ProofUp's household totals,
// a per-applicant card (income A–D + assets, with declared-vs-extracted
// reconciliation and inline override), the document breakdown, and — for
// affordable leases — a global Generate & send action.
export default function IncomeVerificationPanel({ lease }) {
  const [showRaw, setShowRaw] = useState(false);
  // Holding-mode leases submit on the 'HOLD' placeholder unit; the signing forms
  // print the apartment number off the lease, so a real unit must be assigned
  // before generating. Opening this dialog collects it inline (mirrors the
  // server's blockIfHoldUnit gate → 409 code:'HOLD_UNIT').
  const [assignUnitOpen, setAssignUnitOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: [`admin/lease/${lease.id}/income`],
    queryFn: () => api.get(`/v1/admin/lease/${lease.id}/income`),
    enabled: Boolean(lease?.id),
  });

  const report = data?.data?.report ?? null;
  const response = report?.response ?? null;
  const summary = response?.summary ?? null;
  const ticIncome = useMemo(() => data?.data?.ticIncome ?? [], [data]);
  const documents = data?.data?.documents ?? [];
  const applicantVerifications = useMemo(() => data?.data?.applicantVerifications ?? [], [data]);
  // Per-live-in-aide Reasonable-Accommodation approvals. Aides are occupants (not
  // applicants) so they don't appear in the per-applicant rows above; they get
  // their own RA approve/revoke section, and an unapproved aide hard-blocks the
  // package release server-side (verify-and-generate → 409).
  const accommodationApprovals = useMemo(() => data?.data?.accommodationApprovals ?? [], [data]);
  const hasUnapprovedAide = accommodationApprovals.some(a => a.status !== 'approved');
  // Lease-level record: present only for affordable leases. Once status is
  // 'verified' the package has been generated & sent.
  const verification = data?.data?.verification ?? null;
  const affordable = Boolean(lease?.isAffordable || verification);
  // Link-only site-team signer(s) (Owner's Representative) — no email, so the site
  // team signs the TIC via these embed links in-office after the package is sent.
  const ownerSignLinks = useMemo(() => data?.data?.ownerSignLinks ?? [], [data]);
  // HOTMA imputation constants, sourced from the server (mirrors document-signing).
  const assetThreshold = data?.data?.assetThreshold ?? 52787;
  const passbookRate = data?.data?.passbookRate ?? 0.004;
  const packageSent = verification?.status === 'verified';
  // Affordable setup (unit assigned + income/rent restriction saved) must be
  // complete before the package can be generated — the forms need those values.
  // Reported up by AffordableSetupSection off the server's readiness check (the
  // SAME check that hard-blocks release server-side); starts not-ready so the
  // button is gated until the setup queries confirm it's done.
  const [affordableSetup, setAffordableSetup] = useState({ ready: false, missing: [], dirty: false });
  const affordableReady = affordableSetup.ready;
  // The setup card registers a saver here so "Generate & send" can persist any
  // unsaved edits BEFORE generating — what's on screen is what gets sent, not the
  // last-saved state (avoids generating a stale/half-edited setup).
  const setupSaveRef = useRef(null);
  const registerSetupSave = useCallback(fn => {
    setupSaveRef.current = fn;
  }, []);
  // Feature flag: per-income-source ("line") corrections. Off → the legacy A–D
  // bucket editor renders (current behavior).
  const lineMode = Boolean(data?.data?.lineIncomeOverridesEnabled);
  // This property builds its TIC in ResMan, so TenantPort generates none. The
  // staff income overrides feed only the TIC prefill, so the declared-vs-extracted
  // review cues do not need TIC-specific override controls.
  const ticDisabled = Boolean(data?.data?.ticDisabled);

  // Affordable unexplained-deposit review: when the extraction finds bank
  // deposits it can't tie to a declared income source, the lease is flagged for
  // manual review. Staff approve (include the Misc Deposits self-cert) or dismiss
  // (no cert). `depositReview` (the persisted decision) may exist even after the
  // live flag clears, so both drive whether the panel renders.
  const needsManualReview = Boolean(data?.data?.needsManualReview);
  const depositAnalysis = data?.data?.depositAnalysis ?? null;
  const depositReview = data?.data?.depositReview ?? null;

  // member_id in the ICW === prop_person id. Build a name map from the lease's
  // applicants so per-member rows show names instead of raw uuids.
  const memberNames = useMemo(() => {
    const m = {};
    for (const link of lease?.leaseLinks ?? []) {
      const p = link.people;
      if (p?.id) m[p.id] = [p.FirstName, p.LastName].filter(Boolean).join(' ') || p.FirstName || p.id;
    }
    return m;
  }, [lease]);

  // Join TIC income + any existing applicant rows into one row per applicant.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applicants = useMemo(() => {
    const ids = new Set([...ticIncome.map(t => t.personId), ...applicantVerifications.map(v => v.personId)]);
    return [...ids].map(personId => {
      const tic = ticIncome.find(t => t.personId === personId);
      return {
        personId,
        name: memberNames[personId] || personId,
        computed: tic?.computed ?? { a: 0, b: 0, c: 0, d: 0, assetCash: 0, assetIncome: 0 },
        override: tic?.override ?? null,
        declared: tic?.declared ?? null,
        declaredIncome: tic?.declaredIncome ?? null,
        proofupProcessed: tic?.proofupProcessed ?? null,
        incomeMismatch: tic?.incomeMismatch ?? null,
        income: tic?.income ?? null,
      };
    });
  }, [ticIncome, applicantVerifications, memberNames]);

  const unresolvedCount = applicants.filter(a => hasUnresolvedMismatch(a, ticDisabled)).length;

  // Household totals recomputed from the override-aware per-member figures (so the
  // band reflects what staff picked, not ProofUp's raw extraction). Income from
  // assets (TIC line P) is the greater of actual asset income or the household net
  // assets imputed at the passbook rate when they exceed the HOTMA threshold (line O).
  const householdTotals = useMemo(() => {
    const eff = (a, field) => Number(a.override?.[field] ?? a.computed?.[field] ?? 0) || 0;
    const sum = field => applicants.reduce((s, a) => s + eff(a, field), 0);
    const netAssets = sum('assetCash');
    const actualAssetIncome = sum('assetIncome');
    const imputed = netAssets > assetThreshold ? netAssets * passbookRate : 0;
    const incomeFromAssets = Math.max(actualAssetIncome, imputed);
    const baseIncome = sum('a') + sum('b') + sum('c') + sum('d');
    return { netAssets, incomeFromAssets, imputedApplies: imputed > actualAssetIncome, annualIncome: baseIncome + incomeFromAssets };
  }, [applicants, assetThreshold, passbookRate]);

  // Group documents by the applicant who uploaded them (member_id === personId,
  // set when we assemble the ProofUp bundle from each applicant's Upload Center).
  // Anything without a matching applicant falls to a shared "Other documents" list.
  const applicantIds = new Set(applicants.map(a => a.personId));
  const docsByPerson = {};
  const unattributedDocs = [];
  for (const d of documents) {
    if (d.personId && applicantIds.has(d.personId)) (docsByPerson[d.personId] = docsByPerson[d.personId] || []).push(d);
    else unattributedDocs.push(d);
  }

  const filesFailed = Number(summary?.files_failed ?? 0) || 0;
  const extractionErrors = Array.isArray(response?.extraction_errors) ? response.extraction_errors : [];
  const isIncomplete = Boolean(
    report && (report.status === 'partial' || report.status === 'error' || report.status === 'failed' || filesFailed > 0 || extractionErrors.length > 0)
  );
  const aggregationWarnings = summary?.aggregation_warnings ?? [];
  const crossCheckWarnings = summary?.cross_check_warnings ?? [];
  const fileResults = Array.isArray(response?.file_results) ? response.file_results : [];

  const [regenOpen, setRegenOpen] = useState(false);
  const [regenReason, setRegenReason] = useState('');

  const generateMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/lease/${lease.id}/generate`),
    onSuccess: () => {
      toast.success('Signing package generated and sent to the household');
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${lease.id}/income`] });
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${lease.id}/signing-status`] });
    },
    onError: e => {
      // Server safety-net: the release gate 409s on a placeholder unit even if the
      // UI didn't catch it (e.g. a Waitlist unit, or a stale HOLD prop). Open the
      // picker instead of a dead-end toast.
      if (e?.response?.status === 409 && e?.response?.data?.code === 'HOLD_UNIT') {
        setAssignUnitOpen(true);
        return;
      }
      toast.error(e?.response?.data?.message || 'Failed to generate forms');
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: reason => api.post(`/v1/admin/lease/${lease.id}/regenerate-package`, { reason }),
    onSuccess: () => {
      toast.success('Signing package regenerated — applicants were emailed to re-sign');
      setRegenOpen(false);
      setRegenReason('');
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${lease.id}/income`] });
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${lease.id}/signing-status`] });
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${lease.id}/unit-assignment`] });
    },
    onError: e => {
      if (e?.response?.status === 409 && e?.response?.data?.code === 'HOLD_UNIT') {
        setRegenOpen(false);
        setAssignUnitOpen(true);
        return;
      }
      toast.error(e?.response?.data?.message || 'Failed to regenerate the package');
    },
  });

  // The lease still sits on a placeholder unit (holding-mode 'HOLD', or Waitlist).
  const unitIsHold = !lease?.Unit || lease.Unit === 'HOLD' || lease.Unit === 'Waitlist';

  const proceedGenerate = async () => {
    // Fold Save into Generate: persist any unsaved setup edits first, so the
    // package reflects what's on screen. Abort if the save fails (toast shown).
    if (affordableSetup.dirty && setupSaveRef.current) {
      try {
        await setupSaveRef.current();
      } catch {
        return;
      }
    }
    generateMutation.mutate();
  };

  const handleGenerateClick = async () => {
    // A real apartment must be on the lease before the forms are generated — they
    // print the unit number. Collect it inline, then continue the generate flow.
    if (unitIsHold) {
      setAssignUnitOpen(true);
      return;
    }
    await proceedGenerate();
  };

  // After the unit is assigned (dialog PUT + cache invalidation), continue the
  // same generate flow. Doesn't re-check lease.Unit (the prop is still stale until
  // the lease query refetches); the server now sees the assigned unit.
  const handleUnitAssigned = () => {
    setAssignUnitOpen(false);
    proceedGenerate();
  };

  return (
    <Panel
      title="Income Verification (ProofUp)"
      action={
        report ? (
          <Stack direction="row" gap={1} alignItems="center">
            <StatusChip size="small" tone={statusColor[report.status] ?? 'neutral'} label={`Status: ${report.status}`} />
            {report.updatedAt && (
              <Typography variant="caption" color="text.secondary">
                Last run {moment(report.updatedAt).format('MMM D, YYYY h:mm A')}
              </Typography>
            )}
          </Stack>
        ) : null
      }
    >
      {isLoading && <CircularProgress size={20} />}
      {error && (
        <Typography variant="body2" color="error">
          {error?.response?.data?.message || 'Failed to load income report.'}
        </Typography>
      )}

      {!isLoading && !error && !report && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {applicants.length > 0
            ? 'ProofUp did not process any income or asset documents for this lease. Applicant-declared income is shown below and can be used to review and generate forms.'
            : 'ProofUp did not process any income or asset documents for this lease. Generate forms can still proceed once the affordable setup is complete.'}
        </Alert>
      )}

      {!isLoading && !error && (
        <Stack gap={2}>
          {isIncomplete && (
            <Alert severity="warning" sx={{ fontWeight: 500 }}>
              This income figure may be incomplete —{' '}
              {filesFailed > 0 ? `${filesFailed} document(s) failed to process` : `extraction status is "${report.status}"`}. Verify against the source
              documents before relying on it for the TIC.
              {extractionErrors.length > 0 && (
                <Box component="ul" sx={{ m: '4px 0 0', pl: 3 }}>
                  {extractionErrors.map((e, i) => (
                    <li key={i}>
                      <Typography variant="caption">{typeof e === 'string' ? e : JSON.stringify(e)}</Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
          {report?.status === 'failed' && report.errorMessage && (
            <Alert severity="error" sx={{ fontWeight: 500 }}>
              {report.errorMessage}
            </Alert>
          )}

          {/* Household band — override-aware totals + readiness */}
          {applicants.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted }}>
                HOUSEHOLD
              </Typography>
              <Stack direction="row" gap={4} flexWrap="wrap" sx={{ mt: 0.5 }}>
                <Metric label="Household annual income" value={fmtUsd(householdTotals.annualIncome)} emphasize />
                <Metric label="Annual income from assets" value={fmtUsd(householdTotals.incomeFromAssets)} />
                <Metric label="Net family assets" value={fmtUsd(householdTotals.netAssets)} />
              </Stack>
              {householdTotals.imputedApplies && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Income from assets is imputed at the {(passbookRate * 100).toFixed(2)}% passbook rate — net family assets exceed the {fmtUsd(assetThreshold)}{' '}
                  HOTMA threshold.
                </Typography>
              )}
              {affordable && applicants.length > 0 && unresolvedCount > 0 && (
                <Stack direction="row" gap={0.75} alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.colors.warning.fg }}>
                    {unresolvedCount} ICW review cue{unresolvedCount === 1 ? '' : 's'}
                  </Typography>
                </Stack>
              )}
            </Box>
          )}

          {/* Aggregation / dedup warnings — the signal staff act on */}
          {aggregationWarnings.length > 0 && (
            <Alert severity="info">
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Possible double-counting — review before accepting
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                {aggregationWarnings.map((w, i) => (
                  <li key={i}>
                    <Typography variant="body2">{sanitizeWarning(w, memberNames)}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {/* Flagged bank deposits — affordable unexplained-deposit review */}
          {(needsManualReview || depositReview) && (
            <FlaggedDepositsPanel leaseId={lease.id} analysis={depositAnalysis} review={depositReview} memberNames={memberNames} />
          )}

          {/* Per-applicant cards */}
          {applicants.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted }}>
                APPLICANTS
              </Typography>
              <Stack gap={1.5} sx={{ mt: 1 }}>
                {applicants.map(applicant => (
                  <ApplicantCard
                    key={applicant.personId}
                    leaseId={lease.id}
                    applicant={applicant}
                    documents={docsByPerson[applicant.personId] || []}
                    packageSent={packageSent}
                    lineMode={lineMode}
                    ticDisabled={ticDisabled}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Documents not matched to an applicant (and the legacy fallback when
              the payload has no per-doc list). Attributed docs render in cards. */}
          {(documents.length > 0 || fileResults.length > 0) && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted }}>
                Documents processed ({summary?.files_succeeded ?? (documents.length || fileResults.length)}/
                {summary?.files_received ?? (documents.length || fileResults.length)})
              </Typography>
              {documents.length > 0 ? (
                unattributedDocs.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Other documents (not matched to an applicant)
                    </Typography>
                    <DocumentList docs={unattributedDocs} />
                  </>
                )
              ) : (
                <DocumentList
                  docs={fileResults.map((f, i) => ({
                    index: i,
                    filename: f?.filename || f?.file_name,
                    docType: f?.doc_type,
                    docTypeHint: f?.doc_type_hint,
                    viewUrl: null,
                  }))}
                />
              )}
            </Box>
          )}

          {crossCheckWarnings.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted }}>
                Cross-check notes
              </Typography>
              <Box component="ul" sx={{ m: '4px 0 0', pl: 3 }}>
                {crossCheckWarnings.map((w, i) => (
                  <li key={i}>
                    <Typography variant="body2" color="text.secondary">
                      {sanitizeWarning(w, memberNames)}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}

          {/* Live-In Aide Reasonable Accommodation (affordable leases with an aide) */}
          {affordable && accommodationApprovals.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted }}>
                  Reasonable Accommodation (Live-In Aide)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  A live-in aide is an occupant, not an applicant. Their reasonable-accommodation request must be approved before the signing package can be
                  released.
                </Typography>
                <Stack gap={1}>
                  {accommodationApprovals.map(aide => (
                    <AideAccommodationRow key={aide.personId} leaseId={lease.id} aide={aide} disabled={packageSent} />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Unit + income/rent restriction — required before generating, since the
              TIC and other forms need the unit number, income limit, and rent limit. */}
          {affordable && (
            <>
              <Divider />
              <AffordableSetupSection leaseId={lease.id} onReadyChange={setAffordableSetup} onRegisterSave={registerSetupSave} />
            </>
          )}

          {/* Global generate (affordable leases only) */}
          {affordable && (
            <>
              <Divider />
              {packageSent ? (
                <Stack gap={1}>
                  <Alert severity="success">
                    Signing package generated and sent to the household
                    {verification.verifiedBy ? ` · ${verification.verifiedBy}` : ''}
                    {verification.verifiedAt ? ` · ${moment(verification.verifiedAt).format('MMM D, YYYY h:mm A')}` : ''}.
                  </Alert>
                  {ownerSignLinks.map((link, i) => {
                    const isMgmt = link.role === 'Management';
                    return (
                      <Alert
                        key={`${link.role}-${link.submissionGroup}-${i}`}
                        severity="info"
                        action={
                          <Button color="inherit" size="small" href={link.embedSrc} target="_blank" rel="noopener noreferrer">
                            {isMgmt ? 'Sign as Management' : 'Sign as Owner’s Rep'}
                          </Button>
                        }
                      >
                        {isMgmt
                          ? 'Management signature still needed on a Cash Tip Income Certification — open to counter-sign in-office.'
                          : 'Owner’s Representative signature still needed on the TIC — open to counter-sign in-office.'}
                      </Alert>
                    );
                  })}
                  <Stack alignItems="flex-end" gap={0.5}>
                    <Button
                      variant="outlined"
                      onClick={() => setRegenOpen(true)}
                      disabled={regenerateMutation.isPending || hasUnapprovedAide || (!affordableReady && !affordableSetup.dirty)}
                    >
                      {regenerateMutation.isPending ? 'Regenerating…' : 'Regenerate & re-sign'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                      Builds a new package from the latest templates and current application data. Prior signed PDFs are kept; applicants must sign again.
                    </Typography>
                  </Stack>
                  <Dialog open={regenOpen} onClose={regenerateMutation.isPending ? undefined : () => setRegenOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Regenerate &amp; re-sign</DialogTitle>
                    <DialogContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This replaces the current signing package. Applicants will get a new email with signing links and the reason you enter below.
                      </Typography>
                      <TextField
                        autoFocus
                        required
                        fullWidth
                        multiline
                        minRows={3}
                        label="Reason for re-sign"
                        value={regenReason}
                        onChange={e => setRegenReason(e.target.value)}
                        disabled={regenerateMutation.isPending}
                        placeholder="e.g. Updated Student Eligibility form version"
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setRegenOpen(false)} disabled={regenerateMutation.isPending}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        disabled={regenerateMutation.isPending || !regenReason.trim()}
                        onClick={() => regenerateMutation.mutate(regenReason.trim())}
                      >
                        {regenerateMutation.isPending ? 'Regenerating…' : 'Regenerate & send'}
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Stack>
              ) : (
                <Stack alignItems="flex-end" gap={0.5}>
                  <Button
                    variant="contained"
                    onClick={handleGenerateClick}
                    disabled={generateMutation.isPending || hasUnapprovedAide || (!affordableReady && !affordableSetup.dirty)}
                  >
                    {generateMutation.isPending ? 'Generating…' : 'Generate & send forms'}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                    {hasUnapprovedAide
                      ? 'Approve the live-in aide’s reasonable accommodation above before sending the package.'
                      : affordableSetup.dirty
                        ? 'You have unsaved setup changes — they’ll be saved automatically when you generate.'
                        : !affordableReady
                          ? affordableSetup.missing?.length
                            ? `Complete the affordable setup above before generating. Missing: ${affordableSetup.missing.join(', ')}.`
                            : 'Complete the affordable setup above before generating.'
                          : 'Sends the LIHTC signing package to the household for signature.'}
                  </Typography>
                </Stack>
              )}
            </>
          )}

          {report && (
            <>
              <Divider />
              <Link
                component="button"
                underline="hover"
                onClick={() => setShowRaw(v => !v)}
                sx={{ fontSize: 13, color: tokens.colors.primary, alignSelf: 'flex-start' }}
              >
                {showRaw ? 'Hide raw ProofUp response' : 'View raw ProofUp response'}
              </Link>
              <Collapse in={showRaw}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    maxHeight: 300,
                    overflow: 'auto',
                    fontSize: 11,
                    fontFamily: tokens.fonts.mono,
                    color: tokens.colors.ink,
                    backgroundColor: tokens.colors.hover,
                    borderRadius: `${tokens.radius.sm}px`,
                  }}
                >
                  {JSON.stringify(response, null, 2)}
                </Box>
              </Collapse>
            </>
          )}
        </Stack>
      )}

      <AssignUnitDialog open={assignUnitOpen} onClose={() => setAssignUnitOpen(false)} leaseId={lease.id} onAssigned={handleUnitAssigned} />
    </Panel>
  );
}

// Inline unit picker shown when staff try to generate the signing package while
// the lease is still on a placeholder unit. Reuses the same unit-assignment query
// (shared react-query key, so no extra fetch) and PUT /lease/:id/unit as the
// affordable setup card, then hands control back via onAssigned to continue the
// generate flow. Only the unit is set here — move-in/BIN aren't needed for the
// non-TIC forms (they print only the apartment number).
function AssignUnitDialog({ open, onClose, leaseId, onAssigned }) {
  const queryClient = useQueryClient();
  const [unitNumber, setUnitNumber] = useState('');

  const unitQuery = useQuery({
    queryKey: [`admin/lease/${leaseId}/unit-assignment`],
    queryFn: () => api.get(`/v1/admin/lease/${leaseId}/unit-assignment`),
    enabled: open && Boolean(leaseId),
  });
  const units = useMemo(() => unitQuery.data?.data?.units ?? [], [unitQuery.data]);
  const household = unitQuery.data?.data?.household ?? null;

  const bedroomLabel = b => (b == null ? 'size unknown' : b === 0 ? 'Studio' : `${b} BR`);
  const unitLabel = u => {
    let s = `${u.unitNumber} · ${bedroomLabel(u.bedroomCount)}`;
    if (u.fits === false) s += ' — too small';
    else if (u.fits === null) s += ' — size unknown';
    return s;
  };
  const selectedUnit = units.find(u => u.unitNumber === unitNumber) ?? null;

  const assignMutation = useMutation({
    mutationFn: () => api.put(`/v1/admin/lease/${leaseId}/unit`, { unitNumber }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${leaseId}/unit-assignment`] });
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${leaseId}/income`] });
      queryClient.invalidateQueries({ queryKey: [`leaseDetail/${leaseId}`] });
      setUnitNumber('');
      onAssigned?.();
    },
    onError: e => toast.error(e?.response?.data?.message || 'Failed to assign unit'),
  });

  return (
    <Dialog open={open} onClose={assignMutation.isPending ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { maxWidth: '472px', p: 1 } }}>
      <DialogTitle>Assign a unit before generating</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This lease is still on the holding unit. Pick the real apartment — it prints on the signing forms.
        </Typography>
        {unitQuery.isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Autocomplete
            options={units}
            getOptionLabel={unitLabel}
            isOptionEqualToValue={(o, v) => o.unitNumber === v.unitNumber}
            value={selectedUnit}
            onChange={(_, v) => setUnitNumber(v?.unitNumber ?? '')}
            renderInput={params => <TextField {...params} label="Unit" placeholder="Search units" autoFocus />}
          />
        )}
        {household && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            Household of {household.occupants} — units are marked by fit (2 per bedroom + 1).
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={assignMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => assignMutation.mutate()} disabled={!unitNumber || assignMutation.isPending}>
          {assignMutation.isPending ? 'Assigning…' : 'Assign & continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Affordable setup: unit assignment + rent restriction in ONE card with ONE Save.
// Assigning the unit resolves the TIC Part VII rent-limit bedroom lookup and is
// baked into the LIHTC forms at generation time; the program + AMI tier point the
// rent-limit lookup at the right prop_rent_limit_tier rows. Units are marked by fit
// against the household (2-per-bedroom + 1; nothing hidden) and caps preview live.
// Save persists whichever of {unit, rent restriction} changed.
// TIC Part VI — Determination of Program Eligibility. One row per program; staff
// pick the single designation the household satisfies (or leave blank). "Other"
// carries a free-text description. Numeric tokens render as "60%", non-numeric
// (oi/et/eli/vli/li) uppercased. Mirrors the checkbox grid on the TDHCA TIC form.
const designationLabel = t => (/^\d+$/.test(t) ? `${t}%` : t.toUpperCase());

function ProgramEligibilityMatrix({ matrix, progEligible, setProgEligible, progSel, setProgSel, otherText, setOtherText }) {
  if (!matrix.length) return null;
  const toggle = slug =>
    setProgEligible(prev => {
      const next = { ...prev };
      if (next[slug]) delete next[slug];
      else next[slug] = true;
      return next;
    });
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Check each program this household satisfies of the property&apos;s occupancy requirements, then pick its designation.
      </Typography>
      <Stack gap={0.5}>
        {matrix.map(row => {
          const on = Boolean(progEligible[row.slug]);
          return (
            <Stack key={row.slug} direction="row" gap={1} alignItems="center">
              {/* Leading program-eligibility checkbox (the box before each row on the form). */}
              <Checkbox size="small" checked={on} onChange={() => toggle(row.slug)} sx={{ p: 0.5 }} />
              <Typography variant="body2" sx={{ minWidth: 118, color: on ? tokens.colors.ink : tokens.colors.inkMuted }}>
                {row.label}
              </Typography>
              {row.slug === 'other' ? (
                <TextField
                  size="small"
                  placeholder="Description"
                  value={otherText}
                  onChange={e => setOtherText(e.target.value)}
                  disabled={!on}
                  error={on && !otherText.trim()}
                  sx={{ minWidth: 320, flex: 1 }}
                />
              ) : (
                <TextField
                  select
                  size="small"
                  label="Designation"
                  value={progSel[row.slug] ?? ''}
                  disabled={!on}
                  onChange={e =>
                    setProgSel(prev => {
                      const next = { ...prev };
                      if (e.target.value === '') delete next[row.slug];
                      else next[row.slug] = e.target.value;
                      return next;
                    })
                  }
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {row.designations.map(d => (
                    <MenuItem key={d} value={d}>
                      {designationLabel(d)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

function AffordableSetupSection({ leaseId, onReadyChange, onRegisterSave }) {
  const queryClient = useQueryClient();
  const [unitNumber, setUnitNumber] = useState('');
  const [moveInDate, setMoveInDate] = useState(''); // Part I move-in ('YYYY-MM-DD'), site-team entered
  const [binNumber, setBinNumber] = useState(''); // Part I BIN # (free text), site-team entered
  const [setName, setSetName] = useState('');
  const [tier, setTier] = useState(''); // rent AMI tier (Part V rent row + Part VII)
  const [incomeTier, setIncomeTier] = useState(''); // income AMI tier (Part V income row + Part VI)
  const [incomeLimitOverride, setIncomeLimitOverride] = useState(''); // '' = use the tier's auto value
  const [progEligible, setProgEligible] = useState({}); // { [programSlug]: true } — leading program checkbox
  const [progSel, setProgSel] = useState({}); // { [programSlug]: designationToken } (non-"other")
  const [otherText, setOtherText] = useState(''); // Part VI "Other" free-text description
  const emptyRentDetails = {
    tenantPaidRent: '',
    utilityAllowance: '',
    rentalAssistance: '',
    otherCharges: '',
    grossRent: '',
    applicableRentLimit: '', // '' = use the unit's auto value
    // Part VII "Unit meets rent restriction at": a band string ('60'), 'other', or ''.
    unitMeetsRentRestrictionPct: '',
    unitMeetsRentRestrictionOtherPct: '',
    rentalAssistanceFederal: '',
    federalAssistanceType: '',
    federalAssistanceOtherText: '',
  };
  const [rentDetails, setRentDetails] = useState(emptyRentDetails); // Part VII figures + rental-assistance source

  const unitQuery = useQuery({
    queryKey: [`admin/lease/${leaseId}/unit-assignment`],
    queryFn: () => api.get(`/v1/admin/lease/${leaseId}/unit-assignment`),
    enabled: Boolean(leaseId),
  });
  const rentQuery = useQuery({
    queryKey: [`admin/lease/${leaseId}/rent-restriction`],
    queryFn: () => api.get(`/v1/admin/lease/${leaseId}/rent-restriction`),
    enabled: Boolean(leaseId),
  });

  const household = unitQuery.data?.data?.household ?? null;
  const currentUnit = unitQuery.data?.data?.currentUnit ?? null;
  const currentMoveIn = unitQuery.data?.data?.moveInDate ?? null; // 'YYYY-MM-DD' or null
  const currentBin = unitQuery.data?.data?.binNumber ?? null; // BIN # or null
  // In 'direct' mode the applicant already chose a real unit during the apply
  // flow, so staff don't assign one here — hide the unit picker. 'holding' mode
  // lands on 'HOLD' and needs a manual assignment. (unitNumber local state still
  // syncs from currentUnit below, so the rent-restriction section keeps working.)
  const isDirectMode = (unitQuery.data?.data?.unitAssignmentMode ?? 'holding') === 'direct';
  const packageGenerated = unitQuery.data?.data?.packageGenerated ?? false;
  const units = useMemo(() => unitQuery.data?.data?.units ?? [], [unitQuery.data]);

  const available = useMemo(() => rentQuery.data?.data?.available ?? [], [rentQuery.data]);
  const selected = rentQuery.data?.data?.selected ?? null;
  // The property's own AMI designation, used to pre-select the tiers for staff
  // (they can still change them).
  const defaults = rentQuery.data?.data?.default ?? null;
  const catalog = useMemo(() => rentQuery.data?.data?.catalog ?? {}, [rentQuery.data]);
  // TIC Part V income set-aside % options + Part VI program-eligibility matrix —
  // fixed TDHCA structure served by the API (source: lihtc-program-designations).
  const incomeTierOptions = useMemo(() => rentQuery.data?.data?.incomeTierOptions ?? [], [rentQuery.data]);
  const incomeLimitByTier = useMemo(() => rentQuery.data?.data?.incomeLimitByTier ?? {}, [rentQuery.data]);
  const familySize = rentQuery.data?.data?.familySize ?? null;
  const rentTierOptions = useMemo(() => rentQuery.data?.data?.rentTierOptions ?? [], [rentQuery.data]);
  const programMatrix = useMemo(() => rentQuery.data?.data?.programMatrix ?? [], [rentQuery.data]);
  const federalAssistanceTypes = useMemo(() => rentQuery.data?.data?.federalAssistanceTypes ?? [], [rentQuery.data]);
  const rentRestrictionOptions = useMemo(() => rentQuery.data?.data?.rentRestrictionOptions ?? [], [rentQuery.data]);
  // What the applicant declared about rental assistance in the apply flow — shown
  // read-only next to the Federal control so staff can cross-check (NOT auto-filled).
  const applicantRA = rentQuery.data?.data?.applicantRentalAssistance ?? null;
  const applicantRAText = useMemo(() => {
    if (!applicantRA) return null;
    const parts = [];
    if (applicantRA.hasHcvVoucher === true) {
      let hcv = 'Housing Choice Voucher (HCV)';
      const extra = [];
      if (applicantRA.hcvBedroomSize) extra.push(`${applicantRA.hcvBedroomSize} BR`);
      if (applicantRA.hcvIssuingPha) extra.push(`PHA: ${applicantRA.hcvIssuingPha}`);
      if (applicantRA.hcvIsPorting === true) extra.push('porting');
      if (applicantRA.hcvPortabilityExpires) extra.push(`expires ${applicantRA.hcvPortabilityExpires}`);
      if (extra.length) hcv += ` — ${extra.join(', ')}`;
      parts.push(hcv);
    }
    if (applicantRA.publicHousingSection8 === true) parts.push('Public housing / Section 8');
    if (parts.length) return parts.join('; ');
    if (applicantRA.publicHousingSection8 === false && applicantRA.hasHcvVoucher !== true) return 'none declared';
    return null;
  }, [applicantRA]);

  // Prefill from persisted state ONCE per lease, when each query first lands. A
  // post-save refetch must NOT re-run these: the mutation invalidates both queries,
  // and re-hydrating would overwrite (reset) figures the user just typed but that
  // weren't persisted in that save — e.g. the rent PUT was skipped because the tier
  // wasn't set. Guarded by per-query hydration refs, reset when the lease changes.
  const unitHydratedRef = useRef(false);
  const rentHydratedRef = useRef(false);
  useEffect(() => {
    unitHydratedRef.current = false;
    rentHydratedRef.current = false;
  }, [leaseId]);
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!unitQuery.data || unitHydratedRef.current) return;
    unitHydratedRef.current = true;
    setUnitNumber(currentUnit ?? '');
    setMoveInDate(currentMoveIn ?? '');
    setBinNumber(currentBin ?? '');
  }, [unitQuery.data]);
  useEffect(() => {
    if (!rentQuery.data || rentHydratedRef.current) return;
    rentHydratedRef.current = true;
    // Prefer the saved selection; otherwise pre-select the property's designation
    // (defaults) so staff see 60% ready to confirm or change.
    setSetName(selected?.setName ?? defaults?.setName ?? (available.length === 1 ? available[0].setName : ''));
    setTier(selected?.tier != null ? String(selected.tier) : defaults?.tier != null ? String(defaults.tier) : '');
    setIncomeTier(selected?.incomeTier != null ? String(selected.incomeTier) : defaults?.incomeTier != null ? String(defaults.incomeTier) : '');
    setIncomeLimitOverride(selected?.incomeLimit != null ? String(selected.incomeLimit) : '');
    // Part VI program matrix — NO default (ticking a program is a compliance
    // attestation staff must make deliberately). Rehydrate only from saved state.
    const savedProg = selected?.programDesignations ?? [];
    const elig = {};
    const map = {};
    let other = '';
    savedProg.forEach(p => {
      elig[p.program] = true;
      if (p.program === 'other') other = p.text ?? '';
      else if (p.designation) map[p.program] = p.designation;
    });
    setProgEligible(elig);
    setProgSel(map);
    setOtherText(other);
    // Part VII rent details (money fields stored as strings for the inputs). The
    // rent-restriction % defaults to the Part V rent tier when staff haven't set it.
    const rd = selected?.rentDetails ?? null;
    const rentTierVal = selected?.tier != null ? selected.tier : defaults?.tier;
    const restrictionPct =
      rd?.unitMeetsRentRestrictionOtherPct != null
        ? 'other'
        : rd?.unitMeetsRentRestrictionPct != null
          ? String(rd.unitMeetsRentRestrictionPct)
          : rentTierVal != null
            ? String(rentTierVal)
            : '';
    setRentDetails({
      tenantPaidRent: rd?.tenantPaidRent != null ? String(rd.tenantPaidRent) : '',
      utilityAllowance: rd?.utilityAllowance != null ? String(rd.utilityAllowance) : '',
      rentalAssistance: rd?.rentalAssistance != null ? String(rd.rentalAssistance) : '',
      otherCharges: rd?.otherCharges != null ? String(rd.otherCharges) : '',
      grossRent: rd?.grossRent != null ? String(rd.grossRent) : '',
      applicableRentLimit: rd?.applicableRentLimit != null ? String(rd.applicableRentLimit) : '',
      unitMeetsRentRestrictionPct: restrictionPct,
      unitMeetsRentRestrictionOtherPct: rd?.unitMeetsRentRestrictionOtherPct != null ? String(rd.unitMeetsRentRestrictionOtherPct) : '',
      rentalAssistanceFederal: rd?.rentalAssistanceFederal ?? '',
      federalAssistanceType: rd?.federalAssistanceType ?? '',
      federalAssistanceOtherText: rd?.federalAssistanceOtherText ?? '',
    });
  }, [rentQuery.data]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Serialize the Part VI matrix selections back into the stored array shape. A row
  // is included when its leading program box is checked; its designation (if picked)
  // rides along. "Other" carries its free-text description.
  const programDesignations = useMemo(() => {
    const out = [];
    programMatrix.forEach(row => {
      if (!progEligible[row.slug]) return;
      if (row.slug === 'other') out.push({ program: 'other', text: otherText.trim() });
      else out.push({ program: row.slug, ...(progSel[row.slug] ? { designation: progSel[row.slug] } : {}) });
    });
    return out;
  }, [programMatrix, progEligible, progSel, otherText]);
  // "Other" is checked but has no description → not persistable (backend requires text).
  const otherNeedsText = Boolean(progEligible.other) && !otherText.trim();

  // Serialize Part VII rent details: money strings → numbers (omit blanks), plus the
  // rental-assistance source. Null when nothing entered.
  const rentDetailsPayload = useMemo(() => {
    const out = {};
    ['tenantPaidRent', 'utilityAllowance', 'rentalAssistance', 'otherCharges', 'grossRent'].forEach(k => {
      const raw = rentDetails[k];
      if (raw !== '' && raw != null) {
        const n = Number(raw);
        if (!Number.isNaN(n)) out[k] = n;
      }
    });
    // Applicable rent limit is persisted only as an explicit override ('' = use auto).
    if (rentDetails.applicableRentLimit !== '') {
      const n = Number(rentDetails.applicableRentLimit);
      if (!Number.isNaN(n) && n >= 0) out.applicableRentLimit = n;
    }
    // Part VII "Unit meets rent restriction at" — a standard band or the "other %".
    if (rentDetails.unitMeetsRentRestrictionPct === 'other') {
      const n = Number(rentDetails.unitMeetsRentRestrictionOtherPct);
      if (!Number.isNaN(n) && n > 0) out.unitMeetsRentRestrictionOtherPct = n;
    } else if (rentDetails.unitMeetsRentRestrictionPct !== '') {
      out.unitMeetsRentRestrictionPct = Number(rentDetails.unitMeetsRentRestrictionPct);
    }
    if (rentDetails.rentalAssistanceFederal) out.rentalAssistanceFederal = rentDetails.rentalAssistanceFederal;
    // Type + its "other" text only apply when Federal = Yes — never send a lingering
    // type when the answer is No/unset (would check a type box under a "No" on the TIC).
    if (rentDetails.rentalAssistanceFederal === 'yes') {
      if (rentDetails.federalAssistanceType) out.federalAssistanceType = rentDetails.federalAssistanceType;
      if (rentDetails.federalAssistanceType === 'other' && rentDetails.federalAssistanceOtherText.trim()) {
        out.federalAssistanceOtherText = rentDetails.federalAssistanceOtherText.trim();
      }
    }
    return Object.keys(out).length ? out : null;
  }, [rentDetails]);
  // "Other" federal assistance selected but no description → not persistable (only when Federal = Yes).
  const fedOtherNeedsText =
    rentDetails.rentalAssistanceFederal === 'yes' && rentDetails.federalAssistanceType === 'other' && !rentDetails.federalAssistanceOtherText.trim();
  // "Other %" rent restriction selected but no number → not persistable.
  const restrictionOtherNeedsNum = rentDetails.unitMeetsRentRestrictionPct === 'other' && !rentDetails.unitMeetsRentRestrictionOtherPct.trim();

  // Report readiness to the parent (gates "Generate & send forms"). The server
  // computes this off the SAVED lease with the exact same required-field check
  // that hard-blocks release (checkAffordableSetupReadiness), so the button and
  // the server can never disagree. `setupMissing` lists what's still blank.
  const setupReady = rentQuery.data?.data?.setupReady ?? false;
  const setupMissing = useMemo(() => rentQuery.data?.data?.setupMissing ?? [], [rentQuery.data]);
  const ticDisabled = Boolean(rentQuery.data?.data?.ticDisabled);

  // Caps preview follows the Part VII "Unit meets rent restriction at %" (the reality
  // that determines the applicable rent limit) — NOT the Part V intended designation.
  // Blank for the "other %" write-in (no published cap at a custom %).
  const preview = useMemo(() => {
    const pct = rentDetails.unitMeetsRentRestrictionPct;
    if (!setName || !pct || pct === 'other') return [];
    return catalog[`${setName}||${pct}`] ?? [];
  }, [catalog, setName, rentDetails.unitMeetsRentRestrictionPct]);
  // The assigned unit's bedroom count → the single applicable cap (the one that
  // prefills the TIC). The preview above still lists all sizes for reference.
  const selectedUnitBedroom = useMemo(() => units.find(u => u.unitNumber === unitNumber)?.bedroomCount, [units, unitNumber]);
  const autoRentLimit = preview.find(r => r.bedroomCount === selectedUnitBedroom)?.rentLimit;
  const applicableRentLimitDisplay =
    rentDetails.applicableRentLimit !== '' ? rentDetails.applicableRentLimit : autoRentLimit != null ? String(autoRentLimit) : '';

  // Part VII money validation. Gross Rent is the TIC sum (tenant paid rent + utility
  // allowance + non-optional charges; rental assistance is a source, not a component)
  // and must not exceed the applicable rent limit — the LIHTC rent-restriction test.
  // Negatives are never valid. Over-limit + negatives hard-block Save; a sum mismatch
  // is a soft nudge (staff may have a reason). Was previously unvalidated entirely.
  const numOf = v => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const rentComponentSum = numOf(rentDetails.tenantPaidRent) + numOf(rentDetails.utilityAllowance) + numOf(rentDetails.otherCharges);
  const grossRentNum = numOf(rentDetails.grossRent);
  const negativeMoney = ['tenantPaidRent', 'utilityAllowance', 'rentalAssistance', 'otherCharges', 'grossRent'].some(
    k => rentDetails[k] !== '' && numOf(rentDetails[k]) < 0
  );
  const applicableLimitNum = applicableRentLimitDisplay !== '' ? numOf(applicableRentLimitDisplay) : null;
  const grossRentOverLimit = applicableLimitNum != null && rentDetails.grossRent !== '' && grossRentNum > applicableLimitNum;
  const grossRentMismatch = rentDetails.grossRent !== '' && rentComponentSum > 0 && grossRentNum !== rentComponentSum;
  const rentMoneyInvalid = negativeMoney || grossRentOverLimit; // hard-block Save

  const bedroomLabel = b => (b == null ? 'size unknown' : b === 0 ? 'Studio' : `${b} BR`);
  const unitLabel = u => {
    let s = `${u.unitNumber} · ${bedroomLabel(u.bedroomCount)}`;
    if (u.fits === false) s += ' — too small';
    else if (u.fits === null) s += ' — size unknown';
    return s;
  };

  const unitDirty = Boolean(unitNumber) && unitNumber !== (currentUnit ?? '');
  const moveInDirty = moveInDate !== (currentMoveIn ?? '');
  const binDirty = binNumber !== (currentBin ?? '');
  const partIDirty = unitDirty || moveInDirty || binDirty;
  // Part VI income limit: the tier's auto value (for this family size) vs an override.
  // The field shows the override when set, else the auto value — reactive to the tier.
  const autoIncomeLimit = incomeTier !== '' ? incomeLimitByTier[Number(incomeTier)] : undefined;
  const incomeLimitDisplay = incomeLimitOverride !== '' ? incomeLimitOverride : autoIncomeLimit != null ? String(autoIncomeLimit) : '';
  const incomeLimitPayload = incomeLimitOverride === '' ? null : Number(incomeLimitOverride);
  const savedIncomeTier = selected?.incomeTier != null ? String(selected.incomeTier) : '';
  const savedProgJson = JSON.stringify(selected?.programDesignations ?? []);
  const rentFieldsDirty =
    setName !== (selected?.setName ?? '') ||
    tier !== (selected?.tier != null ? String(selected.tier) : '') ||
    incomeTier !== savedIncomeTier ||
    incomeLimitPayload !== (selected?.incomeLimit ?? null) ||
    JSON.stringify(programDesignations) !== savedProgJson ||
    JSON.stringify(rentDetailsPayload) !== JSON.stringify(selected?.rentDetails ?? null);
  // The rent-restriction endpoint requires setName + rent tier, so any income-tier
  // or program-matrix edit is only persistable once those two are set.
  const rentComplete = Boolean(setName) && tier !== '';
  const canSave = (partIDirty || (rentFieldsDirty && rentComplete)) && !otherNeedsText && !fedOtherNeedsText && !restrictionOtherNeedsNum && !rentMoneyInvalid;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const calls = [];
      // Part I (unit + move-in) go to the /unit endpoint; send only what changed.
      if (partIDirty) {
        calls.push(
          api.put(`/v1/admin/lease/${leaseId}/unit`, {
            ...(unitDirty ? { unitNumber } : {}),
            ...(moveInDirty ? { moveInDate: moveInDate || null } : {}),
            ...(binDirty ? { binNumber: binNumber || null } : {}),
          })
        );
      }
      if (rentFieldsDirty && rentComplete) {
        calls.push(
          api.put(`/v1/admin/lease/${leaseId}/rent-restriction`, {
            setName,
            tier: Number(tier),
            incomeTier: incomeTier === '' ? null : Number(incomeTier),
            incomeLimit: incomeLimitPayload,
            programDesignations,
            rentDetails: rentDetailsPayload,
          })
        );
      }
      await Promise.all(calls);
    },
    onSuccess: () => {
      toast.success('Saved');
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${leaseId}/unit-assignment`] });
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${leaseId}/rent-restriction`] });
    },
    onError: e => toast.error(e?.response?.data?.message || 'Failed to save'),
  });

  // Report readiness + unsaved-changes state to the parent, and register a saver
  // it can await before generating. `canSave` doubles as "has persistable unsaved
  // edits"; the server's setupReady/setupMissing gate the button off SAVED state.
  useEffect(() => {
    onReadyChange?.({ ready: setupReady, missing: setupMissing, dirty: canSave });
  }, [setupReady, setupMissing, canSave, onReadyChange]);
  useEffect(() => {
    onRegisterSave?.(() => (canSave ? saveMutation.mutateAsync() : Promise.resolve()));
  }, [canSave, onRegisterSave, saveMutation]);

  const isLoading = unitQuery.isLoading || rentQuery.isLoading;
  const error = unitQuery.error || rentQuery.error;

  return (
    <Box sx={{ mb: 2, p: 2, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.colors.border}` }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: tokens.colors.ink }}>
        Affordable Housing Setup
      </Typography>
      {isLoading && <CircularProgress size={18} sx={{ display: 'block', mt: 1 }} />}
      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error?.response?.data?.message || 'Failed to load affordable setup.'}
        </Typography>
      )}
      {!isLoading &&
        !error &&
        (ticDisabled ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            TIC setup is not required for this property; the TIC is completed in ResMan.
          </Typography>
        ) : (
          <>
            {household && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Household: {household.occupants} occupant{household.occupants === 1 ? '' : 's'} ({household.adults} adult
                {household.adults === 1 ? '' : 's'}
                {household.minors > 0 ? `, ${household.minors} minor${household.minors === 1 ? '' : 's'}` : ''}) · applicable at 2 per bedroom + 1
              </Typography>
            )}
            {/* TIC Part I — the assigned unit. Its bedroom count drives the Part VII rent caps. */}
            <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted, display: 'block', mt: 1.5 }}>
              Unit Assignment (TIC Part I)
            </Typography>
            <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
              {/* Unit picker — holding mode only. In direct mode the unit came from
                the applicant's selection, so there's nothing to (re)assign here. */}
              {!isDirectMode && (
                /* Autocomplete (searchable) — real properties can have hundreds of units. */
                <Autocomplete
                  size="small"
                  options={units}
                  value={units.find(u => u.unitNumber === unitNumber) ?? null}
                  onChange={(_e, val) => setUnitNumber(val?.unitNumber ?? '')}
                  getOptionLabel={unitLabel}
                  isOptionEqualToValue={(o, v) => o.unitNumber === v.unitNumber}
                  sx={{ minWidth: 260 }}
                  renderInput={params => <TextField {...params} label="Unit" placeholder="Search unit…" />}
                />
              )}
              {/* Move-in date — site-team input. Drives the TIC move-in box and the
                Part VII rent-cap effective-date lookup. */}
              <TextField
                size="small"
                type="date"
                label="Move-in date"
                value={moveInDate}
                onChange={e => setMoveInDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              {/* BIN # — TIC Part I Building Identification Number, free text. */}
              <TextField
                size="small"
                label="BIN #"
                value={binNumber}
                onChange={e => setBinNumber(e.target.value)}
                placeholder="e.g. TX-21-00123"
                sx={{ minWidth: 180 }}
              />
              {!currentUnit && (
                <Typography variant="caption" color="text.secondary">
                  Not yet assigned (holding unit)
                </Typography>
              )}
            </Stack>
            {packageGenerated && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                A signature package has already been generated for this lease. Reassigning the unit will not update the existing forms — you'll need to
                regenerate the package for the new unit to appear.
              </Typography>
            )}
            {/* Income/rent restriction is only relevant once a unit is chosen (its
              bedroom count drives the applicable rent caps). */}
            {!unitNumber && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Select a unit to set the income &amp; rent restriction.
              </Typography>
            )}
            {unitNumber &&
              (available.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  No published rent limits found for this property.
                </Typography>
              ) : (
                <>
                  {/* TIC Part V — Household Set-Aside Designation. Two independent rows,
                  each a fixed set-aside %: income (drives Part VI income limit) and
                  rent (drives the Part VII rent row). No limit-set here — that's Part VII. */}
                  <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted, display: 'block', mt: 2 }}>
                    Set-Aside Designation (TIC Part V)
                  </Typography>
                  <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
                    <TextField
                      select
                      size="small"
                      label="Income AMI tier %"
                      value={incomeTier}
                      onChange={e => setIncomeTier(e.target.value)}
                      sx={{ minWidth: 160 }}
                    >
                      {incomeTierOptions.map(t => (
                        <MenuItem key={t} value={String(t)}>
                          {t}%
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField select size="small" label="Rent AMI tier %" value={tier} onChange={e => setTier(e.target.value)} sx={{ minWidth: 160 }}>
                      {rentTierOptions.map(t => (
                        <MenuItem key={t} value={String(t)}>
                          {t}%
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  {/* TIC Part VI — Determination of Program Eligibility. On the form the
                  income limit line sits at the top of Part VI, above the program grid. */}
                  <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted, display: 'block', mt: 2 }}>
                    Determination of Program Eligibility (TIC Part VI)
                  </Typography>
                  {/* Part VI "Current Income Limit per Family Size" — auto from the income
                    tier + family size, editable when the published table doesn't fit. */}
                  <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Current income limit"
                      value={incomeLimitDisplay}
                      onChange={e => setIncomeLimitOverride(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      per family size {familySize ?? '—'}
                      {incomeLimitOverride !== ''
                        ? ' · overridden'
                        : autoIncomeLimit != null
                          ? ` · auto from the ${incomeTier || '—'}% income AMI tier (Section V)`
                          : ` · no published value for the ${incomeTier || '—'}% income tier — enter manually`}
                    </Typography>
                  </Stack>
                  <ProgramEligibilityMatrix
                    matrix={programMatrix}
                    progEligible={progEligible}
                    setProgEligible={setProgEligible}
                    progSel={progSel}
                    setProgSel={setProgSel}
                    otherText={otherText}
                    setOtherText={setOtherText}
                  />

                  {/* TIC Part VII — Rent. The applicable rent-limit table (a property may
                  have several: metro FMR, MTSP, HERA, HOME). Drives the max allowable
                  rent $ caps shown below and prefilled on the form. */}
                  <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted, display: 'block', mt: 2 }}>
                    Rent (TIC Part VII)
                  </Typography>
                  <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
                    <TextField select size="small" label="Rent limit set" value={setName} onChange={e => setSetName(e.target.value)} sx={{ minWidth: 260 }}>
                      {available.map(s => (
                        <MenuItem key={s.setName} value={s.setName}>
                          {s.setName}
                        </MenuItem>
                      ))}
                    </TextField>
                    {/* "Unit Meets Rent Restriction at" — defaults to the Part V rent %,
                    editable by staff (standard band or an "other %" write-in). */}
                    <TextField
                      select
                      size="small"
                      label="Unit meets rent restriction at %"
                      value={rentDetails.unitMeetsRentRestrictionPct}
                      onChange={e =>
                        setRentDetails(prev => ({
                          ...prev,
                          unitMeetsRentRestrictionPct: e.target.value,
                          // clear the write-in unless "other" is chosen
                          unitMeetsRentRestrictionOtherPct: e.target.value === 'other' ? prev.unitMeetsRentRestrictionOtherPct : '',
                        }))
                      }
                      sx={{ minWidth: 220 }}
                    >
                      {rentRestrictionOptions.map(p => (
                        <MenuItem key={p} value={String(p)}>
                          {p}%
                        </MenuItem>
                      ))}
                      <MenuItem value="other">Other %</MenuItem>
                    </TextField>
                    {rentDetails.unitMeetsRentRestrictionPct === 'other' && (
                      <TextField
                        size="small"
                        type="number"
                        label="Other %"
                        value={rentDetails.unitMeetsRentRestrictionOtherPct}
                        onChange={e => setRentDetails(prev => ({ ...prev, unitMeetsRentRestrictionOtherPct: e.target.value }))}
                        error={restrictionOtherNeedsNum}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        sx={{ minWidth: 120 }}
                      />
                    )}
                  </Stack>
                  {/* Rent figures — staff input. Prefill the Part VII $ boxes on the TIC. */}
                  <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1.5 }}>
                    {[
                      ['tenantPaidRent', 'Tenant Paid Rent'],
                      ['utilityAllowance', 'Utility Allowance'],
                      ['rentalAssistance', 'Rental Assistance'],
                      ['otherCharges', 'Other Non-Optional Charges'],
                      ['grossRent', 'Gross Rent for Unit'],
                    ].map(([key, label]) => {
                      const raw = rentDetails[key];
                      const neg = raw !== '' && numOf(raw) < 0;
                      const isGross = key === 'grossRent';
                      const grossErr = isGross && grossRentOverLimit;
                      const helper = isGross
                        ? grossRentOverLimit
                          ? `Exceeds applicable rent limit${applicableLimitNum != null ? ` ($${applicableLimitNum})` : ''}`
                          : grossRentMismatch
                            ? `Should be $${rentComponentSum} (rent + utilities + charges)`
                            : ' '
                        : neg
                          ? 'Must be ≥ 0'
                          : ' ';
                      return (
                        <TextField
                          key={key}
                          size="small"
                          type="number"
                          label={label}
                          value={rentDetails[key]}
                          onChange={e => setRentDetails(prev => ({ ...prev, [key]: e.target.value }))}
                          error={neg || grossErr}
                          helperText={helper}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                          inputProps={{ min: 0 }}
                          sx={{ minWidth: 180 }}
                        />
                      );
                    })}
                  </Stack>
                  {/* What the applicant declared in the apply flow — read-only, so staff
                    can verify before answering the Federal question (not auto-filled). */}
                  {applicantRAText && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Applicant declared (apply flow):{' '}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.ink }}>
                        {applicantRAText}
                      </Typography>
                    </Box>
                  )}
                  {/* Rental-assistance source (Part VII). Type list is always shown so staff
                    see the options; it's enabled once Federal = Yes. */}
                  <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
                    <TextField
                      select
                      size="small"
                      label="Rental assistance is Federal?"
                      value={rentDetails.rentalAssistanceFederal}
                      onChange={e => setRentDetails(prev => ({ ...prev, rentalAssistanceFederal: e.target.value }))}
                      sx={{ minWidth: 300 }}
                    >
                      <MenuItem value="">
                        <em>Not specified</em>
                      </MenuItem>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Federal assistance type"
                      value={rentDetails.federalAssistanceType}
                      onChange={e => setRentDetails(prev => ({ ...prev, federalAssistanceType: e.target.value }))}
                      disabled={rentDetails.rentalAssistanceFederal !== 'yes'}
                      helperText={rentDetails.rentalAssistanceFederal !== 'yes' ? 'Set “Federal? = Yes” to choose a type' : ' '}
                      sx={{ minWidth: 320 }}
                    >
                      <MenuItem value="">
                        <em>Not selected</em>
                      </MenuItem>
                      {federalAssistanceTypes.map(t => (
                        <MenuItem key={t.slug} value={t.slug}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {rentDetails.rentalAssistanceFederal === 'yes' && rentDetails.federalAssistanceType === 'other' && (
                      <TextField
                        size="small"
                        label="Describe other federal assistance"
                        value={rentDetails.federalAssistanceOtherText}
                        onChange={e => setRentDetails(prev => ({ ...prev, federalAssistanceOtherText: e.target.value }))}
                        error={fedOtherNeedsText}
                        sx={{ minWidth: 320, flex: 1 }}
                      />
                    )}
                  </Stack>

                  {preview.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Max allowable rent ({setName} · {rentDetails.unitMeetsRentRestrictionPct}% AMI) — all sizes for reference; the assigned unit is
                        highlighted
                      </Typography>
                      <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {preview.map(r => {
                          const isUnit = r.bedroomCount === selectedUnitBedroom;
                          return (
                            <Stack key={r.bedroomCount} sx={isUnit ? { px: 1, borderRadius: 1, bgcolor: tokens.colors.primarySoft } : {}}>
                              <Typography variant="caption" sx={{ color: isUnit ? tokens.colors.primary : 'text.secondary', fontWeight: isUnit ? 700 : 400 }}>
                                {r.bedroomCount === 0 ? 'Studio' : `${r.bedroomCount} BR`}
                                {isUnit ? ' · unit' : ''}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: isUnit ? 700 : 600, color: isUnit ? tokens.colors.primary : tokens.colors.ink }}>
                                {fmtUsd(r.rentLimit)}
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                  {/* Applicable Rent Limit — the single cap for the assigned unit's bedroom,
                    pre-filled and editable; this is what prefills the TIC Part VII. */}
                  <Stack direction="row" gap={1.5} alignItems="center" sx={{ mt: 1.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      label="Applicable rent limit"
                      value={applicableRentLimitDisplay}
                      onChange={e => setRentDetails(prev => ({ ...prev, applicableRentLimit: e.target.value }))}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      sx={{ minWidth: 200 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {selectedUnitBedroom == null
                        ? 'assign a unit to auto-fill'
                        : `for ${selectedUnitBedroom === 0 ? 'Studio' : `${selectedUnitBedroom} BR`}${
                            rentDetails.applicableRentLimit !== ''
                              ? ' · overridden'
                              : autoRentLimit != null
                                ? ' · auto'
                                : ' · no published cap — enter manually'
                          }`}
                    </Typography>
                  </Stack>
                </>
              ))}

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button size="small" variant="contained" onClick={() => saveMutation.mutate()} disabled={!canSave || saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </>
        ))}
    </Box>
  );
}

// Affordable unexplained-deposit review. Surfaces the server's per-member
// candidate deposits (bank credits it couldn't tie to a declared income source)
// and lets staff approve — which includes the Misc Deposits self-cert in the
// signing package — or dismiss. Mirrors AideAccommodationRow: one mutation per
// decision, toast + income-query invalidation on success. Both actions stay
// available (except the current state's own) so a decision can be reversed.
const DEPOSIT_TONE = { pending: 'warning', approved: 'success', dismissed: 'neutral' };
function FlaggedDepositsPanel({ leaseId, analysis, review, memberNames = {} }) {
  const queryClient = useQueryClient();
  const status = review?.status ?? 'pending';
  const reviewMutation = useMutation({
    mutationFn: nextStatus => api.post(`/v1/admin/leases/${leaseId}/deposit-review`, { status: nextStatus }),
    onSuccess: (_res, nextStatus) => {
      toast.success(nextStatus === 'approved' ? 'Deposits approved — Misc Deposits cert will be included' : 'Deposits dismissed — no cert');
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${leaseId}/income`] });
    },
    onError: e => toast.error(e?.response?.data?.message || 'Failed to update deposit review'),
  });
  const pending = reviewMutation.isPending;
  const approving = pending && reviewMutation.variables === 'approved';
  const dismissing = pending && reviewMutation.variables === 'dismissed';

  const reasons = analysis?.reasons ?? [];
  const perMember = analysis?.per_member ?? [];
  const extractionGaps = analysis?.extraction_gaps ?? [];

  return (
    <Box sx={{ p: 2, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.colors.border}` }}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted }}>
          Flagged Bank Deposits
        </Typography>
        <StatusChip size="small" tone={DEPOSIT_TONE[status] || 'neutral'} label={status.charAt(0).toUpperCase() + status.slice(1)} />
      </Stack>

      {reasons.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Why this was flagged
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {reasons.map((r, i) => (
              <li key={i}>
                <Typography variant="body2">{r}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {perMember.map(member => {
        const name = memberNames[member.member_id] || member.member_id;
        const deposits = member.candidate_deposits ?? [];
        return (
          <Box key={member.member_id} sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {name}
            </Typography>
            {deposits.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No candidate deposits.
              </Typography>
            ) : (
              <Stack gap={0.5} sx={{ mt: 0.5 }}>
                {deposits.map((d, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" alignItems="baseline" gap={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.date ? moment(d.date).format('MMM D, YYYY') : '—'}
                      {d.description || d.source_key ? ` · ${d.description || d.source_key}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                      {fmtUsd(Number(d.amount))}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        );
      })}

      {extractionGaps.length > 0 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Extraction gaps
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {extractionGaps.map((g, i) => (
              <li key={i}>
                <Typography variant="body2">{g}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1} sx={{ mt: 2 }}>
        {status !== 'dismissed' && (
          <Button size="small" color="inherit" variant="outlined" onClick={() => reviewMutation.mutate('dismissed')} disabled={pending}>
            {dismissing ? 'Dismissing…' : 'Dismiss'}
          </Button>
        )}
        {status !== 'approved' && (
          <Button size="small" color="success" variant="contained" onClick={() => reviewMutation.mutate('approved')} disabled={pending}>
            {approving ? 'Approving…' : 'Approve'}
          </Button>
        )}
      </Stack>

      {(status === 'approved' || status === 'dismissed') && (review?.reviewedBy || review?.reviewedAt) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
          {status === 'approved' ? 'Approved' : 'Dismissed'}
          {review?.reviewedBy ? ` by ${review.reviewedBy}` : ''}
          {review?.reviewedAt ? ` · ${moment(review.reviewedAt).format('MMM D, YYYY h:mm A')}` : ''}
        </Typography>
      )}
    </Box>
  );
}

// One live-in aide's Reasonable-Accommodation approve/revoke control. Mirrors the
// per-applicant verify/unverify pattern (same query invalidation). An unapproved
// aide hard-blocks package release server-side (verify-and-generate → 409).
function AideAccommodationRow({ leaseId, aide, disabled }) {
  const queryClient = useQueryClient();
  const approved = aide.status === 'approved';
  const approveMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/lease/${leaseId}/member/${aide.personId}/approve-accommodation`),
    onSuccess: () => {
      toast.success('Reasonable accommodation approved');
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${leaseId}/income`] });
    },
    onError: e => toast.error(e?.response?.data?.message || 'Failed to approve accommodation'),
  });
  const revokeMutation = useMutation({
    mutationFn: () => api.post(`/v1/admin/lease/${leaseId}/member/${aide.personId}/revoke-accommodation`),
    onSuccess: () => {
      toast.success('Reasonable accommodation reopened for review');
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${leaseId}/income`] });
    },
    onError: e => toast.error(e?.response?.data?.message || 'Failed to reopen accommodation'),
  });
  const pending = approveMutation.isPending || revokeMutation.isPending;
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      sx={{ p: 1, borderRadius: `${tokens.radius.md}px`, backgroundColor: tokens.colors.hover }}
    >
      <Stack gap={0.25}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {aide.name || aide.personId}
          <StatusChip size="small" sx={{ ml: 1 }} tone={approved ? 'success' : 'warning'} label={approved ? 'RA approved' : 'RA awaiting'} />
        </Typography>
        {approved && (aide.approvedBy || aide.approvedAt) && (
          <Typography variant="caption" color="text.secondary">
            {aide.approvedBy ? aide.approvedBy : ''}
            {aide.approvedAt ? `${aide.approvedBy ? ' · ' : ''}${moment(aide.approvedAt).format('MMM D, YYYY h:mm A')}` : ''}
          </Typography>
        )}
      </Stack>
      {approved ? (
        <Button size="small" color="inherit" onClick={() => revokeMutation.mutate()} disabled={pending || disabled}>
          {revokeMutation.isPending ? 'Reopening…' : 'Reopen'}
        </Button>
      ) : (
        <Button size="small" variant="contained" color="success" onClick={() => approveMutation.mutate()} disabled={pending || disabled}>
          {approveMutation.isPending ? 'Approving…' : 'Approve RA'}
        </Button>
      )}
    </Stack>
  );
}

// Renders a list of processed documents (filename links to the source doc +
// detected type chip). Shared by the per-applicant card and the global
// "Other documents" fallback.
function DocumentList({ docs }) {
  return (
    <Stack gap={0.5} sx={{ mt: 1 }}>
      {docs.map((d, i) => {
        const label = d.filename || d.docType || `Document ${i + 1}`;
        const type = d.docType || d.docTypeHint;
        return (
          <Stack key={d.index ?? i} direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            {d.viewUrl ? (
              <Link
                href={d.viewUrl}
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{ fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {label}
              </Link>
            ) : (
              <Typography variant="body2" sx={{ color: tokens.colors.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label}
              </Typography>
            )}
            {type && <StatusChip size="small" tone="neutral" dot={false} label={type} sx={{ flexShrink: 0 }} />}
          </Stack>
        );
      })}
    </Stack>
  );
}

function Metric({ label, value, emphasize }) {
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography sx={{ fontWeight: emphasize ? 700 : 600, fontSize: emphasize ? 22 : 16, color: tokens.colors.ink }}>{value}</Typography>
    </Stack>
  );
}

// One household member: per-member TIC figures with inline override and
// declared-vs-extracted reconciliation.
function ApplicantCard({ leaseId, applicant, documents = [], packageSent = false, lineMode = false, ticDisabled = false }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState({});
  const [note, setNote] = useState('');

  const { personId, name, computed, override, declared, declaredIncome, proofupProcessed, income } = applicant;
  // Per-line mode is active only when the flag is on AND the server sent the
  // per-line income view; otherwise fall back to the legacy A–D bucket editor.
  const perLine = lineMode && Boolean(income);

  // Normalized editable rows — one shape for both modes. computedVal is the
  // ProofUp/extracted figure, effectiveVal is what carries to the TIC (override
  // when present), declaredVal is the applicant-typed figure (asset rows only).
  const rows = useMemo(() => {
    const incomeRows = perLine
      ? (income.lines ?? []).map(l => {
          const processed = l.processed !== false; // default true for older payloads
          const declaredAnnual = Number(l.declared ?? 0) || 0;
          const extractedVal = Number(l.extracted ?? 0) || 0;
          // Superset (pre-override): extracted when ProofUp processed this line,
          // else the applicant-declared amount carries through.
          const computedVal = processed ? extractedVal : declaredAnnual;
          return {
            key: l.category,
            label: LINE_META[l.category]?.label ?? l.category,
            group: BUCKET_LABEL[LINE_META[l.category]?.bucket] ?? null,
            computedVal,
            overridden: l.override != null,
            effectiveVal: Number(l.override ?? computedVal) || 0,
            declaredVal: declaredAnnual > 0 ? declaredAnnual : null,
            notProcessed: !processed,
          };
        })
      : TIC_CATEGORIES.filter(c => !ASSET_FIELDS[c.field]).map(c => {
          const declaredAnnual = Number(declaredIncome?.[c.field] ?? 0) || 0;
          return {
            key: c.key,
            label: c.label,
            group: null,
            computedVal: Number(computed?.[c.field] ?? 0) || 0,
            overridden: override?.[c.field] != null,
            effectiveVal: Number(override?.[c.field] ?? computed?.[c.field] ?? 0) || 0,
            // Applicant-declared annual for this bucket — drives the same
            // declared-vs-extracted reconciliation UI the asset rows use.
            declaredVal: declaredAnnual > 0 ? declaredAnnual : null,
            // ProofUp emitted nothing for this bucket → the value shown is the
            // applicant's declared amount, carried through as the superset.
            notProcessed: proofupProcessed ? !proofupProcessed[c.field] : false,
          };
        });
    const assetRows = ASSET_ROWS.map(a => ({
      key: a.key,
      label: a.label,
      group: perLine ? 'Assets' : null,
      computedVal: Number(computed?.[a.field] ?? 0) || 0,
      overridden: override?.[a.field] != null,
      effectiveVal: Number(override?.[a.field] ?? computed?.[a.field] ?? 0) || 0,
      declaredVal: declared?.[a.field] != null ? Number(declared[a.field]) || 0 : null,
    }));
    return [...incomeRows, ...assetRows];
  }, [perLine, income, computed, override, declared, declaredIncome, proofupProcessed]);

  const anyOverride = rows.some(r => r.overridden);
  // Annual income = income buckets A–D + this member's actual income from
  // assets. In per-line mode the buckets come server-computed (so legacy
  // group-level corrections not yet split into lines still count).
  const assetIncomeEff = Number(override?.assetIncome ?? computed?.assetIncome ?? 0) || 0;
  const incomeBucketsTotal = perLine
    ? ['a', 'b', 'c', 'd'].reduce((s, k) => s + (Number(income.effectiveBuckets?.[k] ?? 0) || 0), 0)
    : ['a', 'b', 'c', 'd'].reduce((s, k) => s + (Number(override?.[k] ?? computed?.[k] ?? 0) || 0), 0);
  const memberIncomeTotal = incomeBucketsTotal + assetIncomeEff;
  const legacyBucketOverride = perLine ? income.legacyBucketOverride : null;

  const seed = () => {
    const v = {};
    for (const r of rows) v[r.key] = String(r.effectiveVal.toFixed(2));
    setVals(v);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!editing) seed();
  }, [applicant]);

  const saveMutation = useMutation({
    mutationFn: payload => api.put(`/v1/admin/lease/${leaseId}/income/override`, payload),
    onSuccess: () => {
      toast.success('Income corrections saved');
      queryClient.invalidateQueries({ queryKey: [`admin/lease/${leaseId}/income`] });
      setEditing(false);
    },
    onError: e => toast.error(e?.response?.data?.message || 'Failed to save corrections'),
  });
  const handleSave = () => {
    const batch = [];
    let invalid = false;
    for (const r of rows) {
      const parsed = parseMoney(vals[r.key]);
      if (parsed == null) {
        invalid = true;
        continue;
      }
      // No TIC consumer when disabled, so don't force a trace override on a
      // mismatched row; persist only a real correction.
      const isMismatchRow = !ticDisabled && r.declaredVal != null && cents(r.declaredVal) !== cents(r.computedVal);
      const sameAsComputed = cents(parsed) === cents(r.computedVal);
      if (isMismatchRow) {
        // Force an explicit override for a mismatched row (asset or income) even
        // when staff pick ProofUp's value. It leaves an audit trail for the
        // advisory review cue.
        batch.push({ personId, category: r.key, amount: parsed, note: note.trim() || null });
      } else if (!sameAsComputed) {
        batch.push({ personId, category: r.key, amount: parsed, note: note.trim() || null });
      } else if (r.overridden) {
        batch.push({ personId, category: r.key, amount: null });
      }
    }
    if (invalid) {
      toast.error('Enter a valid dollar amount in every field');
      return;
    }
    if (batch.length === 0) {
      setEditing(false);
      return;
    }
    saveMutation.mutate({ overrides: batch });
  };

  return (
    <Box sx={{ p: 1.5, borderRadius: `${tokens.radius.md}px`, border: `1px solid ${tokens.colors.border}` }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography sx={{ fontWeight: 600, color: tokens.colors.ink }}>{name}</Typography>
        </Stack>
        <Typography sx={{ fontWeight: 600 }}>{fmtUsd(memberIncomeTotal)}</Typography>
      </Stack>

      {editing && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Edit any figure to correct it; the corrected value prefills the TIC. Set a cell back to ProofUp&apos;s number to remove the override.
        </Typography>
      )}

      {legacyBucketOverride && (
        <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
          <Typography variant="caption">
            This member has an older group-level correction (
            {Object.entries(legacyBucketOverride)
              .map(([b, amt]) => `${BUCKET_LABEL[b]}: ${fmtUsd(Number(amt))}`)
              .join(', ')}
            ). Correcting any line below replaces it.
          </Typography>
        </Alert>
      )}

      <Stack gap={editing ? 1 : 0.25} sx={{ mt: 0.5 }}>
        {rows.map((r, i) => {
          const showGroup = r.group && r.group !== rows[i - 1]?.group;
          const overridden = r.overridden;
          const mismatch = r.declaredVal != null && cents(r.declaredVal) !== cents(r.computedVal);
          // An override resolves the mismatch — keep the highlight/chip/picker only
          // while it's unresolved, or whenever the row is being edited. Where the
          // TIC is disabled nothing consumes the picked value, so the whole
          // reconciliation affordance (highlight, chip, declared-vs-ProofUp picker)
          // stays out of the way; staff can still edit a figure by hand.
          const mismatchActive = !ticDisabled && mismatch && (!overridden || editing);
          const pickedAmount = cents(parseMoney(vals[r.key]));
          return (
            <Fragment key={r.key}>
              {showGroup && (
                <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted, mt: i === 0 ? 0 : 0.75 }}>
                  {r.group}
                </Typography>
              )}
              <Stack
                gap={0.5}
                sx={{
                  ...(perLine && !r.group ? {} : perLine ? { pl: 1 } : {}),
                  ...(mismatchActive
                    ? {
                        p: 0.75,
                        borderRadius: `${tokens.radius.sm}px`,
                        backgroundColor: tokens.colors.warning.bg,
                        border: `1px solid ${tokens.colors.warning.main}`,
                      }
                    : {}),
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                  <Stack direction="row" gap={0.75} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {r.label}
                    </Typography>
                    {mismatchActive && <StatusChip size="small" tone="warning" label="mismatch" />}
                    {!mismatchActive && r.notProcessed && r.declaredVal != null && (
                      <Tooltip title="ProofUp did not extract this category — the figure shown is the amount the applicant declared.">
                        <span>
                          <StatusChip size="small" tone="neutral" dot={false} label="Not processed by ProofUp" />
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                  {editing ? (
                    <TextField
                      size="small"
                      value={vals[r.key] ?? ''}
                      onChange={e => setVals(v => ({ ...v, [r.key]: e.target.value }))}
                      InputProps={{
                        startAdornment: (
                          <Typography variant="body2" sx={{ mr: 0.5, color: tokens.colors.inkMuted }}>
                            $
                          </Typography>
                        ),
                      }}
                      helperText={!mismatch && cents(parseMoney(vals[r.key])) !== cents(r.computedVal) ? `ProofUp: ${fmtUsd(r.computedVal)}` : ' '}
                      sx={{ width: 180 }}
                    />
                  ) : (
                    <Stack direction="row" gap={1} alignItems="center">
                      {overridden && (
                        <Tooltip title={`Was ${fmtUsd(r.computedVal)} from ProofUp`}>
                          <span>
                            <StatusChip size="small" tone="info" label="overridden" />
                          </span>
                        </Tooltip>
                      )}
                      <Typography variant="body2" sx={{ color: overridden ? tokens.colors.primary : 'text.secondary', fontWeight: overridden ? 600 : 400 }}>
                        {fmtUsd(r.effectiveVal)}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                {mismatchActive && (
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                      Applicant declared <b>{fmtUsd(r.declaredVal)}</b> · ProofUp extracted <b>{fmtUsd(r.computedVal)}</b>
                      {editing ? ' — pick one:' : ''}
                    </Typography>
                    {editing && (
                      <>
                        <Button
                          size="small"
                          variant={pickedAmount === cents(r.declaredVal) ? 'contained' : 'outlined'}
                          color="warning"
                          onClick={() => setVals(v => ({ ...v, [r.key]: r.declaredVal.toFixed(2) }))}
                        >
                          Declared {fmtUsd(r.declaredVal)}
                        </Button>
                        <Button
                          size="small"
                          variant={pickedAmount === cents(r.computedVal) ? 'contained' : 'outlined'}
                          color="inherit"
                          onClick={() => setVals(v => ({ ...v, [r.key]: r.computedVal.toFixed(2) }))}
                        >
                          ProofUp {fmtUsd(r.computedVal)}
                        </Button>
                      </>
                    )}
                  </Stack>
                )}
              </Stack>
            </Fragment>
          );
        })}
        {editing && (
          <TextField
            size="small"
            fullWidth
            multiline
            label="Reason for correction (optional)"
            value={note}
            onChange={e => setNote(e.target.value)}
            sx={{ mt: 0.5 }}
          />
        )}
      </Stack>

      {documents.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.inkMuted }}>
            Documents ({documents.length})
          </Typography>
          <DocumentList docs={documents} />
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
        {editing ? (
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setEditing(false);
                seed();
              }}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save corrections'}
            </Button>
          </Stack>
        ) : packageSent || ticDisabled ? (
          // No Override on a TIC-disabled property: the corrections it writes feed
          // only the TIC prefill, and this property's TIC comes from ResMan. Same
          // empty slot the card already uses once the package has been sent.
          <Box />
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              seed();
              setEditing(true);
            }}
          >
            {anyOverride ? 'Edit overrides' : 'Override'}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
